<template>
  <SectionPageShell section="support" title="Support">
    <section class="support-faq mb-4" aria-labelledby="support-wordpress-title">
      <div class="support-faq__header">
        <div>
          <h2 id="support-wordpress-title" class="support-faq__title">WordPress-Anleitungen</h2>
          <p class="support-tickets__text mb-0">Hilfe zur Pflege Ihrer WordPress-Website: Texte bearbeiten, Bilder einfügen und Änderungen veröffentlichen.</p>
          <router-link :to="{ name: 'account.support.wordpress' }" class="btn btn-primary mt-3">
            WordPress-Anleitungen öffnen
          </router-link>
        </div>
      </div>
    </section>

    <section class="support-faq mb-4" aria-labelledby="support-faq-title">
      <div class="support-faq__header">
        <div>
          <span class="support-faq__eyebrow">Häufige Fragen</span>
          <h2 id="support-faq-title" class="support-faq__title">Schnelle Antworten zu Ihrem Kundenbereich</h2>
        </div>
      </div>

      <div class="support-faq__list">
        <details v-for="item in faqItems" :key="item.question" class="support-faq__item">
          <summary class="support-faq__question">
            <span>{{ item.question }}</span>
            <Icon icon="fa-pro-solid:chevron-down" class="support-faq__chevron" aria-hidden="true" />
          </summary>
          <div class="support-faq__answer">
            <p class="mb-0">{{ item.answer }}</p>
            <router-link v-if="item.route" :to="{ name: item.route }" class="support-faq__link">
              {{ item.linkLabel }}
            </router-link>
          </div>
        </details>
      </div>
    </section>

    <section class="support-tickets" aria-labelledby="support-tickets-title">
      <div class="support-tickets__header">
        <h2 id="support-tickets-title" class="support-tickets__title">Ihre Support-Tickets</h2>
        <p class="support-tickets__text mb-0">Verfolgen Sie offene Anfragen und lesen Sie Antworten des Supports.</p>
      </div>

      <DataTable
        :value="supportStore.tickets"
        :rows="supportStore.serverOptions.rows"
        :totalRecords="supportStore.totalRecords"
        :loading="supportStore.loading"
        :rowClass="rowClass"
        :lazy="true"
        paginator
        dataKey="id"
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        :rowsPerPageOptions="[10,25,50]"
        currentPageReportTemplate="Zeige {first} bis {last} von {totalRecords}"
        responsiveLayout="scroll"
        @page="onPage"
      >
        <template #empty>
          Keine Tickets gefunden.
        </template>

        <TableColumn field="id" header="#">
          <template #body="{ data }">
            {{ data.id }}
          </template>
        </TableColumn>

        <TableColumn field="title" header="Titel">
          <template #body="{ data }">
            {{ data.title }}
          </template>
        </TableColumn>

        <TableColumn field="state" header="Status">
          <template #body="{ data }">
            <span class="badge" :class="statusBadgeClass(data.state)">
              {{ translatedStatus(data.state) }}
            </span>
          </template>
        </TableColumn>

        <TableColumn field="updated_at" header="Aktualisiert">
          <template #body="{ data }">
            {{ formatDate(data.updated_at, 'datetime') }}
          </template>
        </TableColumn>

        <TableColumn header="">
          <template #body="{ data }">
            <button type="button" class="btn btn-outline-primary btn-sm" @click="openTicket(data.id)">
              Anzeigen
            </button>
          </template>
        </TableColumn>
      </DataTable>

      <div class="support-tickets__footer">
        <router-link :to="{ name: 'account.support.create' }" class="btn btn-warning">
          Ticket erstellen
        </router-link>
      </div>
    </section>
  </SectionPageShell>
</template>

<script>
import SectionPageShell from "@/components/base_type/SectionPageShell.vue";
import { useSupportStore } from "@/stores/support";

export default {
  name: "SupportPage",
  components: {
    SectionPageShell,
  },
  data() {
    return {
      faqItems: [
        {
          question: 'Warum sehe ich noch nicht alle Vermarktungsbereiche?',
          answer: 'Unterkünfte, Angebote, Veranstaltungen und Entdecken werden anhand Ihrer Organisation und aktiver Pakete freigeschaltet. Wenn noch keine Organisation ausgewählt ist oder ein passendes Paket fehlt, sind einzelne Bereiche gesperrt.',
          route: 'packages.index',
          linkLabel: 'Pakete ansehen',
        },
        {
          question: 'Wofür ist der Fragebogen gedacht?',
          answer: 'Der Fragebogen sammelt die wichtigsten Angaben zu Ihrem Unternehmen und zu Ihren Einträgen. Damit kann MANET Ihre Vermarktungsstruktur vorbereiten und die passenden Bereiche im Kundenbereich bereitstellen.',
          route: 'account.survey',
          linkLabel: 'Fragebogen öffnen',
        },
        {
          question: 'Wie lege ich Inhalte wie Unterkünfte, Angebote oder Veranstaltungen an?',
          answer: 'Nach der Freischaltung pflegen Sie Ihre Einträge direkt in den jeweiligen Bereichen. Dort können Sie bestehende Inhalte bearbeiten und neue Einträge anlegen.',
          route: 'dashboard',
          linkLabel: 'Zum Dashboard',
        },
        {
          question: 'Wo lade ich Fotos, Dokumente oder Videos hoch?',
          answer: 'Dateien verwalten Sie in der Mediathek. Dort können Sie neue Medien hochladen, vorhandene Dateien durchsuchen, bearbeiten oder löschen.',
          route: 'media.index',
          linkLabel: 'Mediathek öffnen',
        },
        {
          question: 'Wie ändere oder kündige ich ein Paket?',
          answer: 'Aktive Pakete verwalten Sie im Bereich Pakete. Dort können Sie verfügbare Hochstufungen buchen, laufende Pakete kündigen, gekündigte Pakete reaktivieren oder innerhalb der Frist widerrufen.',
          route: 'packages.index',
          linkLabel: 'Pakete verwalten',
        },
        {
          question: 'Welche Zahlungsmethoden kann ich für Pakete nutzen?',
          answer: 'Im Buchungsprozess stehen PayPal, Apple Pay, SEPA-Lastschrift sowie Kreditkarten von Visa, Mastercard und American Express zur Verfügung.',
          route: 'packages.index',
          linkLabel: 'Pakete öffnen',
        },
        {
          question: 'Warum kann ich ein Paket nicht herunterstufen?',
          answer: 'Eine direkte Herabstufung ist aktuell nicht vorgesehen. Bitte erstellen Sie dafür ein Ticket, damit der Support die Änderung prüfen und mit Ihnen abstimmen kann.',
          route: 'account.support.create',
          linkLabel: 'Ticket erstellen',
        },
        {
          question: 'Wo finde ich meine Rechnungen?',
          answer: 'Im Bereich Rechnungen sehen Sie die aktuelle Rechnung und können ältere Rechnungen über einen Zeitraum suchen. Sofern ein PDF verfügbar ist, können Sie es direkt öffnen.',
          route: 'account.invoices',
          linkLabel: 'Rechnungen öffnen',
        },
        {
          question: 'Was bedeuten die Website-Hinweise?',
          answer: 'Im Bereich Websites sehen Sie hinterlegte Websites und deren Produkttyp. Bei älteren Website-Generationen weist die App auf mögliche Einschränkungen bei Mobiloptimierung, Geschwindigkeit oder Barrierefreiheit hin.',
          route: 'websites.index',
          linkLabel: 'Websites ansehen',
        },
      ],
    }
  },
  setup() {
    return {
      supportStore: useSupportStore(),
    }
  },
  created() {
    this.supportStore.index()
  },
  methods: {
    onPage(event) {
      this.supportStore.serverOptions = event
      this.supportStore.index()
    },
    normalizedStatus(state) {
      const normalizedState = String(state || '').toLowerCase()
      return ['new', 'open', 'closed'].includes(normalizedState) ? normalizedState : 'open'
    },
    translatedStatus(state) {
      const translations = {
        new: 'Neu',
        open: 'Offen',
        closed: 'Geschlossen',
      }

      return translations[this.normalizedStatus(state)]
    },
    statusBadgeClass(state) {
      const badgeClasses = {
        new: 'text-bg-primary',
        open: 'text-bg-warning',
        closed: 'text-bg-secondary',
      }

      return badgeClasses[this.normalizedStatus(state)]
    },
    rowClass(data) {
      return {
        'ticket-row-closed': this.normalizedStatus(data.state) === 'closed',
      }
    },
    openTicket(ticketId) {
      this.$router.push({
        name: 'account.support.show',
        params: {
          ticket_id: ticketId,
        }
      })
    },
  },
}
</script>

<style scoped lang="scss">
.support-faq,
.support-tickets {
  background: #ffffff;
  border: 1px solid #dce4ea;
}

.support-faq {
  overflow: hidden;
}

.support-faq__header,
.support-tickets__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid #e5edf3;
}

.support-faq__eyebrow {
  display: block;
  margin-bottom: 0.25rem;
  color: #5d7183;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.support-faq__title,
.support-tickets__title {
  margin-bottom: 0;
  color: #10273d;
  font-size: 1.15rem;
}

.support-tickets__text {
  margin-top: 0.25rem;
  color: #5d7183;
}

.support-faq__list {
  display: grid;
}

.support-faq__item {
  border-bottom: 1px solid #e8eef4;
}

.support-faq__item:last-child {
  border-bottom: 0;
}

.support-faq__question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-height: 3.25rem;
  padding: 0.9rem 1rem;
  color: #10273d;
  font-weight: 700;
  cursor: pointer;
  list-style: none;
}

.support-faq__question::-webkit-details-marker {
  display: none;
}

.support-faq__chevron {
  flex: 0 0 auto;
  color: #6b7f90;
  transition: transform 0.18s ease;
}

.support-faq__item[open] .support-faq__chevron {
  transform: rotate(180deg);
}

.support-faq__answer {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0 1rem 1rem;
  color: #4f6273;
  line-height: 1.5;
}

.support-faq__link {
  font-weight: 700;
}

.support-tickets {
  overflow: hidden;
}

.support-tickets__footer {
  display: flex;
  justify-content: flex-end;
  padding: 1rem;
  border-top: 1px solid #e5edf3;
}

:deep(.ticket-row-closed) {
  opacity: 0.58;
}

@media (max-width: 767.98px) {
  .support-faq__header,
  .support-tickets__header {
    flex-direction: column;
  }

  .support-tickets__footer .btn {
    width: 100%;
  }
}
</style>
